import * as breakpoint from '@pixelunion/breakpoint';
import HeaderStandard from '@pixelunion/pxs-header-standard';
import { transition } from '@pixelunion/animations';
import DrawerNav from '../components/DrawerNav';
import PredictiveSearch from '../components/PredictiveSearch';
import ThemeUtils from '../utils/ThemeUtils';

export default class StaticHeader extends HeaderStandard {
  constructor(section) {
    super(section);

    this.siteHeaderWrapper = this.el.querySelector('[data-site-header-wrapper]');
    this.mobileSearchButton = this.el.querySelectorAll('[data-search-button]')[0];
    this.desktopSearchButton = this.el.querySelectorAll('[data-search-button]')[1];
    this.headerControls = this.el.querySelectorAll('[data-header-controls]')[1];
    this.headerAccountButton = this.headerControls.querySelector('[data-account-button]');
    this.headerCartButton = this.headerControls.querySelector('[data-cart-button]');
    this.searchContainer = this.el.querySelector('[data-header-search]');
    this.searchForm = this.el.querySelector('[data-header-search-form]');
    this.searchInput = this.el.querySelector('[data-header-search-input]');
    this.searchCloseButton = this.el.querySelector('[data-search-close]');
    this.searchSubmitButton = this.el.querySelector('[data-search-submit]');
    this.headerNavEl = this.el.querySelector('[data-header-nav]');
    this.closedState = 'closed';
    this.openState = 'open';
    this.searchIsOpen = false;
    this.wasMobile = this.isMobile;

    this.drawerTrigger = this.el.querySelector('[data-menu-button]');
    this.drawerNav = new DrawerNav({ drawerTrigger: this.drawerTrigger });

    this.events.register(window, 'resize', ThemeUtils.debounce(() => {
      if ((this.wasMobile && !this.isMobile) || (!this.wasMobile && this.isMobile)) {
        this._closeSearch()
          .then(() => {
            this._initAnimations();
            this._insertSearchForm();
          });
      }
      this.wasMobile = this.isMobile;
    }, 100));

    if (this.desktopSearchButton) {
      this.events.register(this.desktopSearchButton, 'click', event => this._handleSearchButtonClick(event));
    }

    if (this.mobileSearchButton) {
      this.events.register(this.mobileSearchButton, 'click', event => this._handleSearchButtonClick(event));
    }

    if (this.desktopSearchButton || this.mobileSearchButton) {
      this.events.register(this.searchCloseButton, 'click', event => this._closeSearchForm(event));
      this._initAnimations();
      this._insertSearchForm();

      if ('enablePredictiveSearch' in this.searchContainer.dataset) {
        this.predictiveSearch = new PredictiveSearch(this.searchContainer);
      }
    }
  }

  get isMobile() {
    return breakpoint.max('S');
  }

  // Do the checks here instead of in the constructor to account for window resizing
  get navShouldAnimate() {
    return typeof (this.headerNavEl) !== 'undefined'
      && !this.isMobile
      && this.headerNavEl !== null
      && this.searchForm.dataset.headerNavPosition !== 'below';
  }

  _initAnimations() {
    // Responsible for animating the search icon's underline
    // that expands across the search form
    this.desktopSearchButtonAnimation = transition({
      el: this.desktopSearchButton,
      state: this.closedState,
    });

    this.mobileSearchButtonAnimation = transition({
      el: this.mobileSearchButton,
      state: this.closedState,
    });

    // Responsible for animating the appearance of the
    // input's placeholder text
    this.searchInputAnimation = transition({
      el: this.searchInput,
      state: this.closedState,
    });

    // Responsible for the fade in/out of the close button
    // on the search form
    this.searchCloseButtonAnimation = transition({
      el: this.searchCloseButton,
      state: this.closedState,
    });

    // Responsible for the fade in/out of the header icons
    this.searchContainerAnimation = transition({
      el: this.searchContainer,
      state: this.closedState,
    });

    // Responsible for the fade in/out of the header icons and logo on mobile
    this.siteHeaderWrapperAnimation = transition({
      el: this.siteHeaderWrapper,
      state: this.closedState,
    });

    if (this.navShouldAnimate) {
      // Responsible for the fade in/out of the navigation
      this.headerNavAnimation = transition({
        el: this.headerNavEl,
        state: this.openState,
      });
    }

    // Responsible for fade in/out on header control icons
    // (Account, if they're enabled, and cart)
    if (this.headerAccountButton) {
      this.headerAccountButtonAnimation = transition({
        el: this.headerAccountButton,
        state: this.openState,
      });
    }

    if (this.headerCartButton) {
      this.headerCartButtonAnimation = transition({
        el: this.headerCartButton,
        state: this.openState,
      });
    }
  }

  _handleSearchButtonClick(event) {
    event.preventDefault();
    if (this.searchIsOpen && this.searchInput.value.trim().length > 0) {
      this.searchForm.submit();
    } else {
      let controlsWidth = 0;
      controlsWidth = this.headerCartButton
        ? controlsWidth + this.headerCartButton.getBoundingClientRect().width : controlsWidth;
      controlsWidth = this.headerAccountButton
        ? controlsWidth + this.headerAccountButton.getBoundingClientRect().width : controlsWidth;
      controlsWidth = this.desktopSearchButton
        ? controlsWidth + this.desktopSearchButton.getBoundingClientRect().width : controlsWidth;
      this.headerControls.style.setProperty('--header-controls-width', `${controlsWidth}px`);
      this._openSearch();
    }
  }

  _insertSearchForm() {
    // We need to insert the search form directly after the search button
    // so that users using keyboard navigation can access the submit button

    if (this.isMobile) {
      this.mobileSearchButton.parentNode.insertBefore(this.searchContainer, this.mobileSearchButton.nextSibling);
    } else {
      this.desktopSearchButton.parentNode.insertBefore(this.searchContainer, this.desktopSearchButton.nextSibling);
    }
  }

  _openSearch() {
    this._runSearchAnimations(this.openState);
    this._runHeaderAnimations(this.closedState)
      .then(() => this.searchContainerAnimation.animateTo(this.openState))
      .then(() => {
        this.searchInput.focus();
        this.closeSearchEscEvent = this.events.register(window, 'keydown', event => this._closeSearchFormEsc(event));
        this.searchIsOpen = true;
      });
  }

  _closeSearch() {
    const onClose = () => (
      this._runSearchAnimations(this.closedState)
        .then(() => {
          this._runHeaderAnimations(this.openState);

          this.searchContainerAnimation.animateTo(this.closedState);
          if (this.closeSearchEscEvent) this.events.unregister(this.closeSearchEscEvent);
          this.searchIsOpen = false;
          this.searchInput.value = '';
        })
    );

    return this.predictiveSearch && this.predictiveSearch.close()
      ? this.predictiveSearch.close().then(() => onClose())
      : onClose();
  }

  _runHeaderAnimations(state) {
    return new Promise((resolve, reject) => {
      if (this.headerAccountButton) {
        this.headerAccountButtonAnimation.animateTo(state);
      }

      if (this.headerCartButton) {
        this.headerCartButtonAnimation.animateTo(state);
      }

      if (this.navShouldAnimate) {
        this.headerNavAnimation.animateTo(state)
          .then(() => resolve())
          .catch(() => reject());
      } else {
        resolve();
      }
    });
  }

  _runSearchAnimations(state) {
    return new Promise((resolve, reject) => {
      this.searchCloseButtonAnimation.animateTo(state);
      this.searchInputAnimation.animateTo(state);
      this.siteHeaderWrapperAnimation.animateTo(state);

      if (this.isMobile) {
        this.desktopSearchButtonAnimation.animateTo(state)
          .then(() => resolve())
          .catch(() => reject());
        this.mobileSearchButtonAnimation.animateTo(state)
          .then(() => resolve())
          .catch(() => reject());
      } else {
        this.mobileSearchButtonAnimation.animateTo(state)
          .then(() => resolve())
          .catch(() => reject());
        this.desktopSearchButtonAnimation.animateTo(state)
          .then(() => resolve())
          .catch(() => reject());
      }
    });
  }

  _closeSearchForm(event) {
    event.preventDefault();

    this._closeSearch();
  }

  _closeSearchFormEsc(event) {
    if (event.key === 'Escape') {
      this._closeSearchForm(event);
      this.desktopSearchButton.focus();
    }
  }

  onSectionUnload() {
    super.onSectionUnload();
    this.drawerNav.unload();
  }
}
